import { theme } from "@product/scmp-sdk";
import last from "lodash/last";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";
import type { FragmentRefs } from "relay-runtime";

import { AdSlot } from "~/components/advertisement/ad-slots/ad-slot";
import { TargetingPaidType } from "~/components/advertisement/ad-slots/types";
import { getTopicAdsConfigs } from "~/components/topic/topic-ads/ads.config";
import {
  AdsSlotContainer,
  StyledTopStoriesItem,
  TopContentsLeftContainer,
  TopContentsRightContainer,
} from "~/components/topic/topic-top-contents/styles";
import type { topicRestContentsTopic$key } from "~/queries/__generated__/topicRestContentsTopic.graphql";

import { AdsRest, TopContentsRestContainer, TopContentsRestContents } from "./styles";

export type Props = {
  index: number;
  reference: topicRestContentsTopic$key;
  restContents: {
    readonly " $fragmentSpreads": FragmentRefs<
      | "homeSecondaryContentItemContent"
      | "sectionTopLeadPrimaryContentItemContent"
      | "topStoriesItemContent"
    >;
  }[];
};

export const TopicRestContents: FunctionComponent<Props> = ({ index, reference, restContents }) => {
  const data = useFragment(
    graphql`
      fragment topicRestContentsTopic on Topic {
        entityId
        advertZone(version: 2)
      }
    `,
    reference,
  );

  if (!restContents) return null;

  const [first, second, ...rest] = restContents;
  const isShowAds = (index + 1) % 3 === 0; // Show ads in every third block

  const adsConfigs = getTopicAdsConfigs(data?.entityId ?? undefined, data?.advertZone ?? undefined);
  const lastAdsConfig = last(adsConfigs) ?? [];
  const [desktopConfig, mobileConfig] = lastAdsConfig;

  const renderLastItemAd = () => {
    if (!isShowAds) return null;
    return (
      <AdsSlotContainer>
        <AdSlot
          adUnit="d_native2c"
          breakpoint={theme.breakpoints.up("tablet")}
          customizedAdUnitTargeting="dnative-7b"
          sizes={[[1, 1], "fluid"]}
          targeting={{
            paid: TargetingPaidType.Free,
            sctid: data?.entityId,
          }}
          zone={data?.advertZone ?? "default"}
        />
        <AdSlot
          adUnit="m_native2c"
          breakpoint={theme.breakpoints.only("mobile")}
          customizedAdUnitTargeting="dnative-7b"
          sizes={[[1, 1], "fluid"]}
          targeting={{
            paid: TargetingPaidType.Free,
            sctid: data?.entityId,
          }}
          zone={data?.advertZone ?? "default"}
        />
      </AdsSlotContainer>
    );
  };

  return (
    <TopContentsRestContainer key={"rest" + index}>
      <TopContentsRestContents>
        <TopContentsLeftContainer>
          {first && (
            <StyledTopStoriesItem
              reference={first}
              variant="primary"
              withSection={false}
              onClick={() => {}}
            />
          )}
          {second && (
            <StyledTopStoriesItem
              reference={second}
              variant="secondary"
              withSection={false}
              onClick={() => {}}
            />
          )}
        </TopContentsLeftContainer>
        <TopContentsRightContainer>
          {rest.map((item, itemIndex) => (
            <StyledTopStoriesItem
              isShowImage={!isShowAds && itemIndex === rest.length - 1}
              key={itemIndex}
              reference={item}
              variant="secondary"
              withSection={false}
              onClick={() => {}}
            />
          ))}
          {isShowAds && renderLastItemAd()}
        </TopContentsRightContainer>
      </TopContentsRestContents>
      {isShowAds && (
        <AdsRest>
          {desktopConfig && <AdSlot {...desktopConfig} />}
          {mobileConfig && <AdSlot {...mobileConfig} />}
        </AdsRest>
      )}
    </TopContentsRestContainer>
  );
};

TopicRestContents.displayName = "TopicRestContents";
